/**
 * ActivityFeed — a plain-English rollup of the campaign_events timeline.
 *
 * This section reads the *same* events as CampaignTimeline but frames
 * them as a natural-language feed with a single guidance sentence at
 * the bottom for what's next.
 */
import { Card } from "@/shared/ui/card";
import type { CampaignRoomPayload } from "../../types";

// A tiny knowledge base for "what's next" copy, keyed to the most
// recent event. Kept co-located because it is purely presentation.
const NEXT_STEP: Record<string, string> = {
  campaign_created:            "The marketing strategy is being composed.",
  business_goals_understood:   "The marketing strategy is being composed.",
  blueprint_compiled:          "The marketing strategy is ready for your review.",
  strategy_created:            "The marketing strategy is ready for your review.",
  strategy_ready:              "The marketing strategy is ready for your review.",
  strategy_approved:           "The campaign is queued for asset production.",
  generation_started:          "The Creative Studio is producing your assets.",
  generation_completed:        "Assets are ready to be scheduled.",
  scheduling_started:          "Your assets are being placed on the calendar.",
  scheduling_completed:        "Scheduling is complete. Publishing will begin on cue.",
  publishing_started:          "Assets are going out on schedule.",
  publishing_completed:        "Every asset has been published.",
  campaign_completed:          "The campaign is complete.",
  campaign_archived:           "The campaign is archived.",
};

export function ActivityFeed({
  events,
}: {
  events: CampaignRoomPayload["timeline"];
}): JSX.Element {
  const latest = events[events.length - 1];
  const nextStep = latest ? NEXT_STEP[latest.event_name] : undefined;

  return (
    <section data-testid="room-activity-feed">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          Activity
        </p>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
          The latest from your department
        </h2>
      </div>

      <Card className="p-6">
        <ul className="space-y-3">
          {[...events].reverse().slice(0, 6).map((e) => (
            <li
              key={e.id}
              className="text-sm"
              data-testid={`room-activity-${e.event_name}`}
            >
              <span className="font-medium">{e.display_label}</span>
            </li>
          ))}
        </ul>
        {nextStep && (
          <p
            className="text-sm text-muted-foreground mt-6 border-t border-border pt-4"
            data-testid="room-activity-next-step"
          >
            {nextStep}
          </p>
        )}
      </Card>
    </section>
  );
}
